import * as React from "react";
import axios from "axios";

type State = {
	model: modelObject,
	suburbId: string,
	suburbName: string,
	suburbLabel: string,
	suburbPostCode: string,
	hasResults: boolean,
	recommendedCentres: string,
	loading: boolean,
	calculateErrors: string,
	calculateSuccess: boolean,
	suburbChartData: Array<chartDataObject>,
	suburbMin: number,
	suburbMax: number,
	nearbySuburbData: Array<nearbySuburbObject>
	showSuburbChart: boolean,
};

type modelObject = {
	nationalAverageAmount: number,
	nationalChartData: Array<chartDataObject>,
	nationalMin: number,
	nationalMax: number,
}

type chartDataObject = {
	chartLabels: Array<number>,
	chartValues: Array<number>,
	chartColors: Array<string>,
	averageItemPosition: number,
}

type nearbySuburbObject = {
	averageFee: averageFeeObject,
	name: string,
	postcode: number,
	suburbIdHash: string
}

type averageFeeObject = {
	amount: number,
	amountWithCurrency: string,
	averageDailyFeeSpread: {},
	feeAreaType: number,
	max: number,
	min: number,
	nearBySuburbResults: Array<any>,
	perDayText: string,
	rawFees: {},
	rawFeesGrouped: Array<any>,
}

declare var c4kCore: any;

const initialState: State = {
	model: null,
	suburbId: null,
	suburbName: null,
	suburbLabel: null,
	suburbPostCode: null,
	hasResults: false,
	recommendedCentres: null,
	loading: false,
	calculateErrors: null,
	calculateSuccess: false,
	suburbChartData: null,
	suburbMin: null,
	suburbMax: null,
	nearbySuburbData: null,
	showSuburbChart: true,
};

const reducer = (state, action) => {
	const { type, payload } = action;

	switch (type) {
		case "SET_MODEL":
			return {
				...state,
				model: payload
			}

		case "SET_SUBURB":
			return {
				...state,
				suburbId: payload.value,
				suburbLabel: payload.label,
				suburbPostCode: payload.postcode,
				suburbName: payload.suburbName
			}

		case "TOGGLE_LOADING":
			return {
				...state,
				loading: !state.loading
			}

		case "SET_SUBURB_RESULTS":
			return {
				...state,
				hasResults: true,
				suburbChartData: payload.chartData,
				suburbMin: payload.suburbMin,
				suburbMax: payload.suburbMax,
				suburbAverage: payload.suburbAverage,
				nearbySuburbData: payload.nearbySuburbData,
			}

		case "SET_SHOW_SUBURB_CHART":
			return {
				...state,
				showSuburbChart: payload
			}

		default:
			return state;
	}
};

const CostOfCareCalculatorContext = React.createContext(undefined);

function CostOfCareCalculatorProvider(props) {
	const calcBaseUrl = "/costofcarecalculator"

	const [state, dispatch] = React.useReducer(reducer, initialState);

	const HandleSelectSuburb = (value) => {
		dispatch({ type: "TOGGLE_LOADING"})
		dispatch({ type: "SET_SUBURB", payload: value })
		GetSuburbData(value)
	}

	const GetSuburbData = (suburb) => {
		let url = calcBaseUrl + "/getchildcarecosts" + `?postcode=${suburb.postcode}&suburbIdHash=${suburb.value}`

		// AXIOS REQUEST
		axios.get(url).then((response: any) => {

			let data = response.data
			//console.log('RESP DATA::', response)

			if (response.status == 200 && !!response.data.amount) {
				
				let feeSpread = data.rawFeesGrouped
				let maxIndex = Object.entries(feeSpread).length

				let minValue = parseFloat(Object.entries(feeSpread)[0][0])
				let maxValue = parseFloat(Object.entries(feeSpread)[maxIndex - 1][0])

				let averageItemIndex = null
				let chartLabels = []
				let chartValues = []
				let chartColors = []

				// Loop through all prices in the spread and add them to the relevant bound group
				Object.entries(feeSpread).forEach(([key, value], index) => {
					let Key = parseFloat(key)

					if (averageItemIndex == null && (Math.floor(Key) == Math.floor(data.amount))) {
						// If the key amount matches the average and an average chart item has not been set
						averageItemIndex = index
						chartLabels.push(`$${data.amount.toFixed(2).toString()}`)
						chartValues.push(value)
						chartColors.push('rgb(233, 24, 124)')
					} else if (averageItemIndex == null && Key > data.amount) {
						// If the key amount is more than the average and the average chart item has not been set
						averageItemIndex = index
						chartLabels.push(`$${data.amount.toFixed(2).toString()}`)
						chartValues.push(value)
						chartColors.push('rgb(233, 24, 124)')
					}
					else {
						// Create a normal price item
						chartLabels.push(`$${Key.toString()}`)
						chartValues.push(value)
						chartColors.push('rgb(35, 22, 115)')
					}
				});

				// Calc percentage of average item index vs length of data, for positioning of average in the chart
				let averageItemPosition = (averageItemIndex / (Object.entries(feeSpread).length - 1)) * 100

				// Don;t show the chart if there are less than 5 bars
				if (chartValues.length > 5) {
					dispatch({ type: 'SET_SHOW_SUBURB_CHART', payload: true })
				} else {
					dispatch({ type: 'SET_SHOW_SUBURB_CHART', payload: false })
				}

				let payload = {
					suburbAverage: data.amount,
					chartData: {
						chartLabels: chartLabels,
						chartValues: chartValues,
						chartColors: chartColors,
						averageItemPosition: averageItemPosition.toFixed(0)
					},
					suburbMin: minValue,
					suburbMax: maxValue,
					averageItemIndex: averageItemIndex,
					nearbySuburbData: data.nearBySuburbResults
				}

				dispatch({ type: 'TOGGLE_LOADING' })
				dispatch({ type: 'SET_CALCULATE_SUCCESS', payload: true })
				dispatch({ type: "SET_SUBURB_RESULTS", payload: payload})

				GetRecommendedCentres(suburb.postcode, suburb.label, suburb.value)

				// Fire the GA event for submission
				//TrackSubmitAnalyticsEvent();
			} else {
				dispatch({
					type: 'SET_CALCULATE_ERROR',
					payload: {
						message: data.errors[0]
					}
				})
				dispatch({ type: "TOGGLE_LOADING" });

			}

		})
			.catch(error => {

				let errorMessage = {
					message: "Something went wrong please try again"
				}

				dispatch({
					type: 'SET_CALCULATE_ERROR',
					payload: errorMessage
				})
				dispatch({ type: 'TOGGLE_LOADING' })

			})
	}

	const GetRecommendedCentres = (postCode, label, suburbId) => {
		let url = `/recommended/centres?postcode=${postCode}&suburbIdHash=${suburbId}`
		//Do Axios
		axios.get(url).then((response: any) => {

			// Drop the html data into the c4k recommends container
			if (!!response.data?.RecommendedCentreHtml && response.data?.RecommendedCentreHtml != "") {
				let container = document.getElementById('coc-c4k-recommends')
				container.innerHTML = response.data.RecommendedCentreHtml
				c4kCore.initImageLazyLoading(container)
				let title = document.querySelector('#recommends-title')
				if (!!title) {
					title.innerHTML = `<div class="text-skin-heading text-2xl mb-4 px-6 lg:px-0">Centres in ${label} you might be interested in</div>`
				}
				let viewMoreButton = document.querySelector('#recommends-view-more')
				if (!!viewMoreButton) {
					viewMoreButton.innerHTML = `<div class="text-center pt-12"><a href=${response.data.SearchUrl} class="cta-secondary button inline-block" target="_blank">View more results</a><div>`
				}

			}
		})
			.catch(error => {
				console.log(error)
			})
		// END AXIOS
	}

	return (

		/*Must add any function names to the provider value object*/
		/*You can also customise the values ie name = state.name*/
		/*You will use the keys from this object to unpack the values in components*/
		<CostOfCareCalculatorContext.Provider
			value={{
				state,
				dispatch,
				HandleSelectSuburb
			}}>
			{props.children}
		</CostOfCareCalculatorContext.Provider>
	);
}

export { CostOfCareCalculatorContext, CostOfCareCalculatorProvider };